import React, { useState, useEffect } from 'react';
import { Header } from './components/Header';
import { CassetteDeck } from './components/CassetteDeck';
import { SearchView } from './components/SearchView';
import { TrackCard } from './components/TrackCard';
import { FullPlayer } from './components/FullPlayer';
import { BottomNav } from './components/BottomNav';
import { MiniPlayerBar } from './components/MiniPlayerBar';
import { ProfileView } from './components/ProfileView';
import { SettingsView } from './components/SettingsView';
import { Track, Album, PlayerTab, DspState, AppTab, UserSettings } from './types';
import { tg } from './services/telegram';
import { audioEngine } from './services/audioEngine';
import { monochromeApi } from './services/monochromeApi';
import { userStore } from './services/userStore';
import { QueueRackIcon } from './components/icons/HardwareIcons';

export const App: React.FC = () => {
  // Navigation & Modal state
  const [activeAppTab, setActiveAppTab] = useState<AppTab>('deck');
  const [isFullPlayerOpen, setIsFullPlayerOpen] = useState(false);
  const [activePlayerTab, setActivePlayerTab] = useState<PlayerTab>('player');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [searchAlbums, setSearchAlbums] = useState<Album[]>([]);

  // Audio engine state bindings
  const [currentTrack, setCurrentTrack] = useState<Track | null>(audioEngine.currentTrack);
  const [isPlaying, setIsPlaying] = useState<boolean>(audioEngine.isPlaying);
  const [currentTime, setCurrentTime] = useState<number>(audioEngine.currentTime);
  const [duration, setDuration] = useState<number>(audioEngine.duration);
  const [volume, setVolume] = useState<number>(audioEngine.volume);
  const [isShuffle, setIsShuffle] = useState<boolean>(audioEngine.isShuffle);
  const [isRepeat, setIsRepeat] = useState<boolean>(audioEngine.isRepeat);
  const [telemetry, setTelemetry] = useState(audioEngine.telemetry);
  const [dsp, setDsp] = useState<DspState>(audioEngine.dsp);

  // User store state bindings
  const [favorites, setFavorites] = useState<Track[]>(userStore.favorites);
  const [history, setHistory] = useState<Track[]>(userStore.history);
  const [recentSearches, setRecentSearches] = useState<string[]>(userStore.recentSearches);
  const [settings, setSettings] = useState<UserSettings>(userStore.settings);

  // Init Telegram SDK & parse bot deep-links
  useEffect(() => {
    tg.init();
    userStore.rebindUserScope();
    userStore.applyTheme(userStore.settings.theme);

    const params = new URLSearchParams(window.location.search);
    const startParam = (window as any).Telegram?.WebApp?.initDataUnsafe?.start_param || '';
    const trackIdParam =
      params.get('track') || (startParam.startsWith('track_') ? startParam.replace('track_', '') : '');
    const queryParam =
      params.get('q') || (startParam.startsWith('q_') ? decodeURIComponent(startParam.replace('q_', '')) : '');
    const tabParam = params.get('tab') as AppTab | null;

    if (tabParam && ['deck', 'search', 'profile', 'settings'].includes(tabParam)) {
      setActiveAppTab(tabParam);
    }
    if (queryParam) {
      setSearchQuery(queryParam);
      setActiveAppTab('search');
    }
    if (trackIdParam) {
      monochromeApi.getTrackById(trackIdParam).then((found) => {
        if (found) {
          audioEngine.loadTrack(found);
        }
      });
    }

    const unlockOnTouch = () => {
      audioEngine.unlock();
      window.removeEventListener('pointerdown', unlockOnTouch);
      window.removeEventListener('touchstart', unlockOnTouch);
    };
    window.addEventListener('pointerdown', unlockOnTouch, { passive: true });
    window.addEventListener('touchstart', unlockOnTouch, { passive: true });
  }, []);

  // BackButton handling
  useEffect(() => {
    if (isFullPlayerOpen) {
      return tg.onBackClick(() => setIsFullPlayerOpen(false));
    } else if (activeAppTab !== 'deck') {
      return tg.onBackClick(() => setActiveAppTab('deck'));
    }
  }, [isFullPlayerOpen, activeAppTab]);

  // Subscribe to Audio Engine changes
  useEffect(() => {
    const unsub = audioEngine.subscribe(() => {
      setCurrentTrack(audioEngine.currentTrack);
      setIsPlaying(audioEngine.isPlaying);
      setCurrentTime(audioEngine.currentTime);
      setDuration(audioEngine.duration);
      setVolume(audioEngine.volume);
      setIsShuffle(audioEngine.isShuffle);
      setIsRepeat(audioEngine.isRepeat);
      setTelemetry({ ...audioEngine.telemetry });
      setDsp({ ...audioEngine.dsp });
    });
    return unsub;
  }, []);

  // Subscribe to User Store changes
  useEffect(() => {
    const unsub = userStore.subscribe(() => {
      setFavorites([...userStore.favorites]);
      setHistory([...userStore.history]);
      setRecentSearches([...userStore.recentSearches]);
      setSettings({ ...userStore.settings });
    });
    return unsub;
  }, []);

  // Search effect with debounce, AbortController and instant state sync
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) {
      setTracks([]);
      setSearchAlbums([]);
      setIsLoadingSearch(false);
      return;
    }

    setIsLoadingSearch(true);
    const controller = new AbortController();

    const timer = setTimeout(async () => {
      try {
        const res = await monochromeApi.search(q, controller.signal);
        if (!controller.signal.aborted) {
          setTracks(res.tracks);
          setSearchAlbums(res.albums);
        }
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          console.warn('Search error:', err);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoadingSearch(false);
        }
      }
    }, 280);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [searchQuery]);

  const handleSelectTrack = (track: Track) => {
    audioEngine.play(track);
  };

  const handlePlayAllFavorites = () => {
    if (favorites.length > 0) {
      userStore.setQueue(favorites);
      audioEngine.play(favorites[0]);
      setActiveAppTab('deck');
    }
  };

  const openSheetWithTab = (tab: PlayerTab) => {
    setActivePlayerTab(tab);
    setIsFullPlayerOpen(true);
  };

  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => {
      setToastMessage((cur) => (cur === message ? null : cur));
    }, 4500);
  };

  const handleSendFlacToBot = async (trackToSend?: Track | null) => {
    const target = trackToSend || currentTrack;
    if (!target) return;

    tg.haptic('medium');
    showToast(`⏳ Подготовка FLAC для «${target.title}»...`);

    const res = await monochromeApi.requestFlacToBot(target.id);
    if (res.ok) {
      tg.haptic('success');
      showToast(`🎧 FLAC «${target.artist} — ${target.title}» отправлен в чат с ботом!`);
    } else {
      tg.haptic('error');
      showToast(`⚠️ ${res.error || 'Не удалось отправить FLAC в бота'}`);
      if (res.error && res.error.includes('Telegram-бота')) {
        tg.showAlert(res.error);
      }
    }
  };

  return (
    <div className="tma-shell">
      {/* Realtime Telemetry Toast Notification */}
      {toastMessage && (
        <div className="telemetry-toast-banner" onClick={() => setToastMessage(null)}>
          <div className="toast-dot" />
          <span className="toast-text">{toastMessage}</span>
        </div>
      )}

      {/* Top Rack Faceplate Header */}
      <Header
        sampleRate={telemetry.sampleRate}
        bitDepth={telemetry.bitDepth}
      />

      {/* Main Studio Scroll Container */}
      <main className="deck-scroll">
        {/* 1. DECK TAB */}
        {activeAppTab === 'deck' && (
          <>
            {/* Volumetric 3D Cassette Bay + VFD Bargraph + Transport Keys + 3D Brass Knobs */}
            <CassetteDeck
              track={currentTrack}
              isPlaying={isPlaying}
              currentTime={currentTime}
              duration={duration}
              volume={volume}
              telemetry={telemetry}
              dsp={dsp}
              onSetBass={(db) => audioEngine.setBass(db)}
              onSetTreble={(db) => audioEngine.setTreble(db)}
              onSetBalance={(pan) => audioEngine.setBalance(pan)}
              onTogglePlay={() => audioEngine.togglePlay()}
              onSeek={(sec) => audioEngine.seek(sec)}
              onNext={() => audioEngine.next()}
              onPrev={() => audioEngine.prev()}
              onSetVolume={(v) => audioEngine.setVolume(v)}
              onOpenFullPlayer={() => openSheetWithTab('player')}
              onSendFlacToBot={handleSendFlacToBot}
            />

            {/* Current Loaded Queue (No search box here, clean studio deck) */}
            <section className="tape-vault-card stagger-item stagger-4">
              <div className="vault-header">
                <span className="vault-header-left">
                  <QueueRackIcon size={14} color="var(--amber)" />
                  <span>Очередь воспроизведения</span>
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '11.5px', color: 'rgba(255, 255, 255, 0.5)', fontWeight: 500, letterSpacing: '0.01em' }}>
                    {audioEngine.queue.length} {audioEngine.queue.length === 1 ? 'трек' : audioEngine.queue.length < 5 ? 'трека' : 'треков'}
                  </span>
                  {audioEngine.queue.length > 0 && (
                    <button
                      type="button"
                      className="btn-action-secondary"
                      style={{ padding: '2px 8px', fontSize: '10px' }}
                      onClick={() => {
                        tg.haptic('medium');
                        userStore.clearQueue();
                      }}
                    >
                      Очистить
                    </button>
                  )}
                </div>
              </div>

              {audioEngine.queue.length > 0 ? (
                <div className="cassette-spine-list">
                  {audioEngine.queue.map((t, idx) => (
                    <TrackCard
                      key={`queue-${t.id}-${idx}`}
                      track={t}
                      index={idx}
                      isActive={currentTrack ? t.id === currentTrack.id : false}
                      isPlaying={isPlaying}
                      onSelect={() => handleSelectTrack(t)}
                      onSendFlacToBot={handleSendFlacToBot}
                    />
                  ))}
                </div>
              ) : (
                <div className="profile-empty-card" style={{ padding: '24px 16px', margin: '8px 0' }}>
                  <div className="empty-icon-wrap">
                    <QueueRackIcon size={24} color="var(--amber)" />
                  </div>
                  <div className="empty-title">Очередь воспроизведения пуста</div>
                  <div className="empty-desc" style={{ marginBottom: '14px' }}>
                    Здесь сохраняются только те мастер-ленты, которые вы воспроизводили в этой сессии.
                  </div>
                  <button
                    type="button"
                    className="btn-action-primary"
                    style={{ margin: '0 auto', maxWidth: '220px' }}
                    onClick={() => {
                      tg.haptic('light');
                      setActiveAppTab('search');
                    }}
                  >
                    Перейти к поиску треков
                  </button>
                </div>
              )}
            </section>
          </>
        )}

        {/* 2. SEARCH TAB */}
        {activeAppTab === 'search' && (
          <SearchView
            query={searchQuery}
            onQueryChange={setSearchQuery}
            isLoading={isLoadingSearch}
            tracks={tracks}
            albums={searchAlbums}
            currentTrack={currentTrack}
            isPlaying={isPlaying}
            onSelectTrack={(t) => {
              handleSelectTrack(t);
              if (searchQuery.trim().length >= 2) {
                userStore.addRecentSearch(searchQuery);
              }
            }}
            recentSearches={recentSearches}
            onSendFlacToBot={handleSendFlacToBot}
          />
        )}

        {/* 3. PROFILE TAB */}
        {activeAppTab === 'profile' && (
          <ProfileView
            currentTrack={currentTrack}
            isPlaying={isPlaying}
            favorites={favorites}
            history={history}
            settings={settings}
            onSelectTrack={handleSelectTrack}
            onPlayAllFavorites={handlePlayAllFavorites}
            onSendFlacToBot={handleSendFlacToBot}
          />
        )}

        {/* 4. SETTINGS TAB */}
        {activeAppTab === 'settings' && (
          <SettingsView
            settings={settings}
            dsp={dsp}
          />
        )}
      </main>

      {/* Floating Mini Player Bar (shown on Search, Profile and Settings tabs) */}
      {activeAppTab !== 'deck' && (
        <MiniPlayerBar
          track={currentTrack}
          isPlaying={isPlaying}
          currentTime={currentTime}
          duration={duration}
          onTogglePlay={() => audioEngine.togglePlay()}
          onNext={() => audioEngine.next()}
          onOpenPlayer={() => openSheetWithTab('player')}
        />
      )}

      {/* Fixed Bottom Navigation Bar */}
      <BottomNav
        activeTab={activeAppTab}
        onTabChange={(tab) => setActiveAppTab(tab)}
        favoritesCount={favorites.length}
      />

      {/* Expandable Studio Reference Console Drawer */}
      <FullPlayer
        isOpen={isFullPlayerOpen}
        activeTab={activePlayerTab}
        onTabChange={setActivePlayerTab}
        onClose={() => setIsFullPlayerOpen(false)}
        track={currentTrack}
        isPlaying={isPlaying}
        currentTime={currentTime}
        duration={duration}
        volume={volume}
        isShuffle={isShuffle}
        isRepeat={isRepeat}
        queue={audioEngine.queue}
        onTogglePlay={() => audioEngine.togglePlay()}
        onSeek={(sec) => audioEngine.seek(sec)}
        onNext={() => audioEngine.next()}
        onPrev={() => audioEngine.prev()}
        onSetVolume={(v) => audioEngine.setVolume(v)}
        onToggleShuffle={() => audioEngine.toggleShuffle()}
        onToggleRepeat={() => audioEngine.toggleRepeat()}
        onSelectTrack={(t) => handleSelectTrack(t)}
        onSendFlacToBot={handleSendFlacToBot}
      />
    </div>
  );
};
